import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import GlobalStyles from '@mui/material/GlobalStyles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  applyFilterValues,
  filterValuesFromSearch,
  filterValuesToSearch,
  DEFAULT_WORKSPACE_FORMATS,
  formatInstant,
  isApiError,
  links,
  resolveFormattingLocale,
  type ReportFilterValue,
} from '@eis/core/reporting';
import {
  EmptyState,
  ErrorState,
  PageHeader,
  PageLayout,
  RequirePermission,
  cardSurfaceSx,
  usePermissions,
} from '@eis/patterns';
import {
  FilterValueInput,
  QueryRunState,
  RANGE_OP,
  browserTimeZone,
  displayFieldName,
  filterValuesComplete,
  normalizeNumber,
  type FilterValueType,
} from '@eis/patterns/reporting';
import { reportingPermissions as p } from '../../../permissions';
import { asDocument, usedFields } from '../../../lib/document';
import { codeMessage } from '../../../lib/codes';
import { hasResultData, workspaceFormatsOf } from '../../../lib/result';
import {
  isPrivateReport,
  useCancelQuery,
  useCustomDataset,
  useDataset,
  useDatasetFieldValues,
  useDuplicateReport,
  useExportReport,
  useQueryPolling,
  useQueryRowsLoader,
  useReport,
  useRunReport,
  useWorkspaceSettings,
} from '../api/viewer';
import {
  filterGroups,
  nextSort,
  sortFromSearch,
  sortToSearch,
  viewSearch,
  zoneFromSearch,
} from '../model/filters';
import { ReportResult } from './ReportResult';

export interface ReportViewerPageProps {
  readonly code: string;
  /** The URL's `f.<index>` entries (D-2): the viewer's editable filter values. */
  readonly search: Readonly<Record<string, string>>;
  readonly onSearchChange: (search: Record<string, string>) => void;
  /** Renders the print layout (title, filters, "Daten vom", the result; no chrome) on screen. */
  readonly printMode?: boolean;
}

/** The print stylesheet (E-3): the browser's "Als PDF sichern" prints the report, not the console. */
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '#app-nav, header, .MuiAppBar-root, .no-print, [data-testid="jobs-tray"]': {
          display: 'none !important',
        },
        'body, #root, main': {
          background: 'white !important',
          overflow: 'visible !important',
          height: 'auto !important',
        },
        '.print-only': { display: 'block !important' },
      },
    }}
  />
);

/**
 * The viewer (SDD-024 R-3, U-4, Q-1; mockup boards 3 and 10): the latest published version, run on
 * open and polled; the fixed filters dashed, the editable ones changeable with their values in the
 * URL (`f.<index>`), the run state with cancel and "Neu ausführen" (reuse bypassed once), the table
 * / pivot / chart, "Daten anzeigen" under a chart, "Daten vom", and the actions the persona holds:
 * Duplizieren (any reader, R-7), Exportieren (CSV / XLSX as jobs, PDF by printing), Bearbeiten
 * (author or `reports.manage`).
 */
export function ReportViewerPage({
  code,
  search,
  onSearchChange,
  printMode = false,
}: ReportViewerPageProps): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const permissions = usePermissions();
  const report = useReport(code);
  const settings = useWorkspaceSettings();
  const run = useRunReport();
  const cancel = useCancelQuery();
  const duplicate = useDuplicateReport();
  const exportReport = useExportReport();
  const loadRows = useQueryRowsLoader();
  const [queryId, setQueryId] = useState<string | undefined>(undefined);
  const polling = useQueryPolling(queryId);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exported, setExported] = useState<string | undefined>(undefined);
  // The filter being changed: its first clause and how many it spans (2 for a range).
  const [editing, setEditing] = useState<{ index: number; span: number } | undefined>(undefined);
  const [draft, setDraft] = useState<string[]>([]);
  const [showData, setShowData] = useState(false);
  // Further pages of THIS query (Q-3); a new query starts from its own first page.
  const [loaded, setLoaded] = useState<{ id?: string; rows: (string | null)[][]; token: string }>({
    rows: [],
    token: '',
  });

  const doc = asDocument(report.data?.document);
  const saved = useMemo(() => doc?.filters ?? { join: 'ALL' as const, clauses: [] }, [doc]);
  const values: ReportFilterValue[] = useMemo(
    () => filterValuesFromSearch(search, saved),
    [search, saved],
  );
  const applied = useMemo(() => applyFilterValues(saved, values), [saved, values]);
  const tokens = filterGroups(saved, applied, values, locale);
  // The viewer's own sort (a table header) and time zone travel in the URL beside the values.
  const sort = useMemo(() => sortFromSearch(search), [search]);
  const zone = zoneFromSearch(search);
  const setFilterSearch = (next: Record<string, string>): void => {
    onSearchChange({ ...viewSearch(search), ...next });
  };
  const runKey = `${code}:${String(report.data?.publishedVersion ?? 0)}:${JSON.stringify(values)}:${JSON.stringify(sort)}`;
  const lastRun = useRef('');
  const datasetName = doc?.dataset.kind === 'CATALOG' ? doc.dataset.name : undefined;
  const catalog = useDataset(datasetName);
  const custom = useCustomDataset(doc?.dataset.kind === 'CUSTOM' ? doc.dataset.code : undefined);
  const fieldTypes = useMemo(
    () =>
      new Map(
        (doc?.dataset.kind === 'CUSTOM'
          ? (custom.data?.fields ?? [])
          : (catalog.data?.fields ?? [])
        ).map((f) => [f.name ?? '', (f.type ?? 'STRING') as FilterValueType]),
      ),
    [doc?.dataset.kind, custom.data, catalog.data],
  );
  const valuesOf = useDatasetFieldValues(datasetName);
  const editedField = editing === undefined ? '' : (saved.clauses[editing.index]?.field ?? '');
  const loadEditedValues = useCallback(
    (prefix: string) => valuesOf(editedField, prefix),
    [valuesOf, editedField],
  );

  const start = (bypassReuse = false): void => {
    if (!permissions.can(p.queries.run)) return;
    run.mutate(
      {
        code,
        filterValues: values.map((v) => ({ index: v.index, values: [...v.values] })),
        bypassReuse,
        sort,
      },
      {
        onSuccess: (query) => {
          setQueryId(query.id);
        },
      },
    );
  };

  // Q-1: the viewer runs on open, and again whenever an editable value changes in the URL.
  useEffect(() => {
    if (doc === undefined || lastRun.current === runKey) return;
    lastRun.current = runKey;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, doc === undefined]);

  if (report.isLoading) {
    return (
      <PageLayout header={<PageHeader title={t('viewer.breadcrumb')} />}>
        <Skeleton variant="rounded" height={320} />
      </PageLayout>
    );
  }
  if (report.isError) {
    if (isPrivateReport(report.error)) {
      const message = codeMessage(t, 'PRIVATE_REPORT');
      return (
        <PageLayout header={<PageHeader title={t('noAccess.title')} />}>
          <EmptyState
            title={t('noAccess.title')}
            description={`${message.title}. ${message.hint}`}
          />
        </PageLayout>
      );
    }
    return (
      <PageLayout header={<PageHeader title={t('viewer.breadcrumb')} />}>
        <ErrorState
          description={
            isApiError(report.error) && report.error.status === 404
              ? t('viewer.notFound')
              : t('viewer.loadError')
          }
          onRetry={() => void report.refetch()}
        />
      </PageLayout>
    );
  }
  const head = report.data;
  if (head === undefined || doc === undefined) {
    return (
      <PageLayout header={<PageHeader title={head?.name ?? t('viewer.breadcrumb')} />}>
        <EmptyState title={t('viewer.unpublished')} />
      </PageLayout>
    );
  }

  const query = polling.query;
  const more =
    loaded.id === queryId && queryId !== undefined
      ? loaded
      : { rows: [] as (string | null)[][], token: query?.nextPageToken ?? '' };
  const failure = query?.error?.code;
  const message = codeMessage(t, failure);
  const known = new Set((catalog.data?.fields ?? []).map((f) => f.name));
  const calculated = new Set((doc.calculatedFields ?? []).map((c) => c.name));
  const stale =
    datasetName === undefined || catalog.data === undefined
      ? []
      : usedFields(doc).filter((f) => !known.has(f) && !calculated.has(f));
  const dataAsOf =
    query?.dataAsOf === undefined
      ? undefined
      : formatInstant(query.dataAsOf, locale, undefined, { precise: false });
  const draftVersion = (head.publishedVersion ?? 0) + 1;
  // The zone switch shows only when a date-time is on screen or filtered.
  const hasDateTime = usedFields(doc).some((f) => fieldTypes.get(f) === 'DATETIME');
  const canEdit = head.editable === true && permissions.can(p.reports.update);

  const actions = (
    <Stack direction="row" spacing={1} className="no-print" sx={{ flexWrap: 'wrap' }}>
      <RequirePermission anyOf={[p.reports.view]}>
        <Button
          size="small"
          variant="outlined"
          disabled={duplicate.isPending}
          onClick={() => {
            duplicate.mutate(
              {
                code,
                ...(head.publishedVersion === undefined || head.publishedVersion === 0
                  ? {}
                  : { version: head.publishedVersion }),
              },
              {
                onSuccess: (copy) => {
                  const target = copy.code ?? '';
                  void navigate({
                    href: permissions.can(p.reports.update)
                      ? links.reporting.editReport(target)
                      : links.reporting.report(target),
                  });
                },
              },
            );
          }}
        >
          {t('viewer.duplicate')}
        </Button>
      </RequirePermission>
      <RequirePermission anyOf={[p.reports.export]}>
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon />}
          aria-haspopup="menu"
          onClick={(event) => {
            setExportAnchor(event.currentTarget);
          }}
        >
          {t('viewer.export')}
        </Button>
      </RequirePermission>
      <Menu
        anchorEl={exportAnchor}
        open={exportAnchor !== null}
        onClose={() => {
          setExportAnchor(null);
        }}
      >
        {(['CSV', 'XLSX'] as const).map((format) => (
          <MenuItem
            key={format}
            dense
            onClick={() => {
              setExportAnchor(null);
              exportReport.mutate(
                {
                  code,
                  format,
                  filterValues: values.map((v) => ({ index: v.index, values: [...v.values] })),
                  label: head.name ?? code,
                  sort,
                },
                {
                  onSuccess: () => {
                    setExported(head.name ?? code);
                  },
                },
              );
            }}
          >
            {format === 'CSV' ? t('viewer.exportCsv') : t('viewer.exportXlsx')}
          </MenuItem>
        ))}
        <MenuItem
          dense
          onClick={() => {
            setExportAnchor(null);
            window.print();
          }}
        >
          {t('viewer.exportPrint')}
        </MenuItem>
      </Menu>
      {canEdit && (
        <Button
          size="small"
          variant="contained"
          onClick={() => void navigate({ href: links.reporting.editReport(code) })}
        >
          {head.hasDraft === true
            ? t('viewer.editDraft', { version: draftVersion })
            : t('viewer.edit')}
        </Button>
      )}
    </Stack>
  );

  const header = (
    <PageHeader
      title={head.name ?? code}
      breadcrumbs={[
        { label: t('viewer.breadcrumb'), to: links.reporting.reports() },
        { label: head.name ?? code },
      ]}
      description={[
        t('viewer.datasetLine', { name: head.datasetName ?? '' }),
        dataAsOf === undefined ? undefined : t('viewer.dataAsOf', { at: dataAsOf }),
      ]
        .filter((x) => x !== undefined)
        .join(' · ')}
      actions={printMode ? undefined : actions}
    />
  );

  return (
    <PageLayout header={header}>
      {printStyles}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={(head.publishedVersion ?? 0) > 0 ? 'success' : 'default'}
            label={
              (head.publishedVersion ?? 0) > 0
                ? t('viewer.published', { version: head.publishedVersion })
                : t('viewer.unpublished')
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={t(`visibility.${(head.visibility ?? 'PRIVATE') as 'PRIVATE' | 'WORKSPACE'}`)}
          />
          {dataAsOf !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {t('viewer.dataAsOf', { at: dataAsOf })}
            </Typography>
          )}
        </Stack>
        {exported !== undefined && (
          <Alert
            severity="info"
            className="no-print"
            onClose={() => {
              setExported(undefined);
            }}
          >
            {t('viewer.exportStarted', { label: exported })}
          </Alert>
        )}
        {stale.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>{t('viewer.staleTitle')}</AlertTitle>
            {t('viewer.staleBody', {
              fields: stale.map((f) => `„${displayFieldName(f)}“`).join(', '),
            })}
          </Alert>
        )}
        {tokens.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
            aria-label={t('viewer.filters')}
            role="group"
          >
            {tokens.map((token, i) => (
              <Stack key={token.index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {i > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t(`viewer.join.${applied.join}`)}
                  </Typography>
                )}
                {token.editable && !printMode ? (
                  <Chip
                    size="small"
                    color={token.changed ? 'primary' : 'default'}
                    label={`${token.label} ${token.text}`}
                    data-filter-label={token.label}
                    onClick={() => {
                      const clause = applied.clauses[token.index];
                      setDraft(
                        token.span === 2
                          ? [clause?.value ?? '', applied.clauses[token.index + 1]?.value ?? '']
                          : [
                              ...(clause?.values ??
                                (clause?.value === undefined ? [] : [clause.value])),
                            ],
                      );
                      setEditing({ index: token.index, span: token.span });
                    }}
                    {...(token.changed
                      ? {
                          onDelete: () => {
                            setFilterSearch(
                              filterValuesToSearch(
                                values.filter(
                                  (v) =>
                                    v.index < token.index || v.index >= token.index + token.span,
                                ),
                                saved,
                              ),
                            );
                          },
                        }
                      : {})}
                    aria-label={t('viewer.filterEdit', { label: token.label })}
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    sx={{ borderStyle: 'dashed' }}
                    label={`${token.label} ${token.text}`}
                    title={t('viewer.filterFixed')}
                    data-filter-label={token.label}
                  />
                )}
              </Stack>
            ))}
            {values.length > 0 && !printMode && (
              <Button
                size="small"
                className="no-print"
                onClick={() => {
                  setFilterSearch({});
                }}
              >
                {t('viewer.filterReset')}
              </Button>
            )}
          </Stack>
        )}
        {!printMode && hasDateTime && (
          <Stack direction="row" spacing={1} className="no-print" sx={{ alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.timeZone')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={zone}
              aria-label={t('viewer.timeZone')}
              onChange={(_, next: 'LOCAL' | 'UTC' | null) => {
                if (next === null) return;
                const { tz: _tz, ...rest } = search;
                onSearchChange(next === 'UTC' ? { ...rest, tz: 'utc' } : rest);
              }}
            >
              <ToggleButton value="LOCAL">
                {t('viewer.timeZoneLocal', { zone: browserTimeZone() })}
              </ToggleButton>
              <ToggleButton value="UTC">{t('viewer.timeZoneUtc')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}
        {!printMode && (
          <Box className="no-print">
            <QueryRunState
              phase={polling.phase === 'IDLE' ? 'QUEUED' : polling.phase}
              elapsedSeconds={polling.elapsedSeconds}
              {...(queryId === undefined ? {} : { queryId })}
              empty={query?.state === 'SUCCEEDED' && !hasResultData(query)}
              {...(failure === undefined
                ? {}
                : { errorCode: failure, errorTitle: message.title, errorHint: message.hint })}
              {...(query?.error?.message === undefined
                ? {}
                : { engineMessage: query.error.message })}
              warning={failure === 'TOO_MANY_GROUPS'}
              stats={{
                ...(query?.stats?.engineMs === undefined
                  ? {}
                  : { engineMs: Number(query.stats.engineMs) }),
                ...(query?.stats?.scannedBytes === undefined
                  ? {}
                  : { scannedBytes: Number(query.stats.scannedBytes) }),
                reused: query?.stats?.reused === true,
              }}
              onCancel={() => {
                if (queryId !== undefined) cancel.mutate({ id: queryId });
              }}
              onRerun={() => {
                start(true);
              }}
            />
          </Box>
        )}
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <ReportResult
            doc={doc}
            query={query}
            settings={{
              ...(workspaceFormatsOf(settings.data?.document) ?? DEFAULT_WORKSPACE_FORMATS),
              timeZone: zone === 'UTC' ? 'UTC' : browserTimeZone(),
            }}
            sort={sort.length > 0 ? sort : (doc.table?.sort ?? [])}
            {...(printMode
              ? {}
              : {
                  onSort: (by: string) => {
                    const { sort: _sort, ...rest } = search;
                    onSearchChange({ ...rest, ...sortToSearch(nextSort(sort, by)) });
                  },
                })}
            moreRows={more.rows}
            hasMore={more.token !== ''}
            onLoadMore={() => {
              if (queryId === undefined || more.token === '') return;
              void loadRows(queryId, more.token).then((page) => {
                setLoaded({
                  id: queryId,
                  rows: [...more.rows, ...page.rows],
                  token: page.nextPageToken,
                });
              });
            }}
            showData={showData || printMode}
          />
          {doc.reportType === 'CHART' &&
            query?.state === 'SUCCEEDED' &&
            (query.rows?.length ?? 0) > 0 &&
            !printMode && (
              <Button
                size="small"
                className="no-print"
                sx={{ mt: 1 }}
                onClick={() => {
                  setShowData((v) => !v);
                }}
              >
                {showData ? t('viewer.hideData') : t('viewer.showData')}
              </Button>
            )}
        </Paper>
      </Stack>
      <Dialog
        open={editing !== undefined}
        onClose={() => {
          setEditing(undefined);
        }}
        maxWidth="xs"
        fullWidth
        // Named on the dialog itself, not by its title: the title unmounts while the dialog fades out.
        slotProps={{ paper: { 'aria-label': t('viewer.filters') } }}
      >
        {editing !== undefined && (
          <>
            <DialogTitle id="filter-value-title">
              {t('viewer.filterDialogTitle', {
                label: tokens.find((g) => g.index === editing.index)?.label ?? '',
              })}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <FilterValueInput
                  type={fieldTypes.get(editedField) ?? 'STRING'}
                  op={editing.span === 2 ? RANGE_OP : (saved.clauses[editing.index]?.op ?? '==')}
                  values={draft}
                  onChange={setDraft}
                  {...(datasetName !== undefined && fieldTypes.get(editedField) === 'STRING'
                    ? { loadValues: loadEditedValues }
                    : {})}
                  timeZone={zone}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                onClick={() => {
                  setEditing(undefined);
                }}
              >
                {t('viewer.filterCancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={
                  !filterValuesComplete(
                    fieldTypes.get(editedField) ?? 'STRING',
                    editing.span === 2 ? RANGE_OP : (saved.clauses[editing.index]?.op ?? '=='),
                    draft,
                  )
                }
                onClick={() => {
                  const clean = draft.map((v) =>
                    fieldTypes.get(editedField) === 'NUMBER' ? normalizeNumber(v) : v.trim(),
                  );
                  const changed =
                    editing.span === 2
                      ? [
                          { index: editing.index, values: [clean[0] ?? ''] },
                          { index: editing.index + 1, values: [clean[1] ?? ''] },
                        ]
                      : [{ index: editing.index, values: clean.filter((v) => v !== '') }];
                  const next = [
                    ...values.filter(
                      (v) => v.index < editing.index || v.index >= editing.index + editing.span,
                    ),
                    ...changed,
                  ];
                  setEditing(undefined);
                  setFilterSearch(filterValuesToSearch(next, saved));
                }}
              >
                {t('viewer.filterApply')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PageLayout>
  );
}
